import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dialog as SheetPrimitive } from "radix-ui"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"
import { Button } from "../button/button"

function Sheet({ ...props }: ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetPortal({
  ...props
}: ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetClose({ ...props }: ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

type SheetContentProps = ComponentProps<typeof SheetPrimitive.Content> & {
  /**
   * Figma zeigt nur „Sheet Template Desktop" (rechts andockend) und „Sheet
   * Template Mobile" (Vollbild) — die übrigen Seiten folgen shadcns
   * Referenzverhalten. Laut Figma ohne Eckenradius.
   */
  side?: "top" | "right" | "bottom" | "left"
  /**
   * Breite ab `md` (≥ 768 px) für `side="left" | "right"`: `"third"` = 1/3
   * (Standard), `"half"` = 1/2 des Viewports. Darunter ist der Sheet immer Vollbild
   * (Figma „Sheet Template Mobile").
   */
  size?: "third" | "half"
}

function SheetContent({
  className,
  side = "right",
  size = "third",
  children,
  ...props
}: SheetContentProps) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        data-size={size}
        className={cn(
          "fixed z-50 flex flex-col overflow-hidden border-separators-non-opaque bg-backgrounds-secondary outline-hidden",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
          "data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:max-h-[80vh] data-[side=bottom]:border-t data-[side=bottom]:data-[state=closed]:slide-out-to-bottom data-[side=bottom]:data-[state=open]:slide-in-from-bottom",
          "data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:max-h-[80vh] data-[side=top]:border-b data-[side=top]:data-[state=closed]:slide-out-to-top data-[side=top]:data-[state=open]:slide-in-from-top",
          "data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-full data-[side=right]:data-[state=closed]:slide-out-to-right data-[side=right]:data-[state=open]:slide-in-from-right md:data-[side=right]:border-l",
          "data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-full data-[side=left]:data-[state=closed]:slide-out-to-left data-[side=left]:data-[state=open]:slide-in-from-left md:data-[side=left]:border-r",
          "md:data-[size=third]:data-[side=left]:w-1/3 md:data-[size=third]:data-[side=right]:w-1/3 md:data-[size=half]:data-[side=left]:w-1/2 md:data-[size=half]:data-[side=right]:w-1/2",
          className
        )}
        {...props}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

/**
 * Figma „Sheet Header": Rückpfeil (schließt/navigiert per `SheetClose`),
 * alles Weitere (Titel, Zeitstempel, CTAs, `SheetInfo`, …) ist bewusst
 * `children` — die konkrete Zusammensetzung (Figma-Varianten „default",
 * „mobile", „with sheet info") ist fachlicher Anwendungsfall, keine feste
 * Prop-API der Komponente.
 */
type SheetHeaderProps = ComponentProps<"div"> & {
  /**
   * Figma-Variante „mobile": CTAs rechts im Header. Nur unterhalb von `md`
   * sichtbar — ab `md` gehören die CTAs in den `SheetFooter`, der auf Mobile
   * ausgeblendet ist.
   */
  actions?: ReactNode
}

function SheetHeader({
  className,
  children,
  actions,
  ...props
}: SheetHeaderProps) {
  return (
    <div
      data-slot="sheet-header"
      className={cn(
        "flex w-full shrink-0 items-center gap-2 border-b border-separators-non-opaque bg-backgrounds-secondary p-2 md:px-4 md:py-2",
        className
      )}
      {...props}
    >
      <SheetClose asChild>
        <Button variant="ghost" iconOnly aria-label="Zurück">
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
      </SheetClose>
      {children}
      {actions && (
        <div
          data-slot="sheet-header-actions"
          className="ml-auto flex shrink-0 items-center gap-2 md:hidden"
        >
          {actions}
        </div>
      )}
    </div>
  )
}

type SheetInfoProps = ComponentProps<"div"> & {
  /** Figma „Avatar" — als `ReactNode`, damit z. B. `Avatar` aus `@e2n/ui/v2` (Größe `xs`) genutzt werden kann. */
  avatar: ReactNode
  title: ReactNode
  subtitle: ReactNode
  /** Figma „Container" rechts (Kategorie-Auswahl + Dauer) — bleibt `children`, da fachlicher Inhalt der App. */
  trailing?: ReactNode
}

/**
 * Figma „Sheet info" (nur in der Variante „with sheet info" sichtbar):
 * Personenbezug (Avatar + Name + Datum) und optionaler `trailing`-Bereich,
 * z. B. Kategorie-Auswahl und Dauer. Wird als `children` in `SheetHeader`
 * gerendert, ist aber keine feste Prop davon — siehe `SheetHeader`.
 */
function SheetInfo({
  className,
  avatar,
  title,
  subtitle,
  trailing,
  ...props
}: SheetInfoProps) {
  return (
    <div
      data-slot="sheet-info"
      className={cn(
        "flex w-full items-center gap-3 border-t border-separators-non-opaque px-4 py-2",
        className
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {avatar}
        <div className="flex min-w-0 flex-col">
          <p className="truncate text-sm leading-5 font-semibold text-brand-blue-100">
            {title}
          </p>
          <p className="truncate text-xs leading-4 text-labels-primary">
            {subtitle}
          </p>
        </div>
      </div>
      {trailing && (
        <div className="flex shrink-0 items-center gap-3">{trailing}</div>
      )}
    </div>
  )
}

/** Figma „Content": padding- und stilloser Slot für den eigentlichen Inhalt. */
function SheetBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-body"
      className={cn("flex-1 overflow-y-auto p-4", className)}
      {...props}
    />
  )
}

/**
 * Figma „Footer": CTAs sind `children` (z. B. `Button` aus `@e2n/ui/v2`).
 * Erst ab `md` sichtbar — auf Mobile liegen die CTAs per `actions` im
 * `SheetHeader` (Figma „Sheet Template Mobile" hat keinen Footer).
 */
function SheetFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "mt-auto hidden w-full shrink-0 items-center justify-end gap-2 border-t border-separators-non-opaque bg-backgrounds-secondary px-6 py-3 md:flex",
        className
      )}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "truncate text-sm leading-5 font-semibold text-labels-primary",
        className
      )}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-xs leading-4 text-labels-secondary", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetInfo,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
}
